import { setFulfilled, setPending, setRejected } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { DelegationTeamsLoadAction, EditUserDelegateFulfilledAction } from '../../domains/delegation/actions.types';
import {
  AddUserDelegateFulfilledAction,
  RemoveUserDelegateFulfilledAction,
} from '../../domains/delegationAddRemove/actions.types';

import { CurrentDelegationTeams } from './types';

const initialState: CurrentDelegationTeams = {
  pending: false,
  fulfilled: undefined,
  rejected: undefined,
};

const addUserDelegate = (state: CurrentDelegationTeams, options: AddUserDelegateFulfilledAction['payload']) => {
  if (!state.fulfilled) {
    return state;
  }

  const team = state.fulfilled[options.onBehalfOfUserId];
  const newMember = { nodeId: options.delegateUserId, delegateRole: options.delegateRole, canPostToStoryline: false };

  return team
    ? {
        ...state,
        fulfilled: {
          ...state.fulfilled,
          [options.onBehalfOfUserId]: {
            ...team,
            members: [...team.members, newMember],
          },
        },
      }
    : {
        ...state,
        fulfilled: {
          ...state.fulfilled,
          [options.onBehalfOfUserId]: {
            ownerId: options.onBehalfOfUserId,
            members: [newMember],
            hasNetworkWideAudience: false,
            viewerIsOwner: options.onBehalfOfUserId === options.currentUserId,
            viewerCanManageTeam: true,
            viewerCanPostAnnouncementToStoryline: false,
            viewerCanPostToStoryline: options.onBehalfOfUserId === options.currentUserId,
            isOwnerLeader: false,
          },
        },
      };
};

const removeUserDelegate = (state: CurrentDelegationTeams, options: RemoveUserDelegateFulfilledAction['payload']) => {
  if (!state.fulfilled) {
    return state;
  }

  const team = state.fulfilled[options.onBehalfOfUserId];
  if (!team) {
    return state;
  }

  const currentUserDeclinedDelegation = options.delegateUserId === options.currentUserId;
  if (currentUserDeclinedDelegation) {
    const { [options.onBehalfOfUserId]: teamToRemove, ...rest } = state.fulfilled;

    return {
      ...state,
      fulfilled: rest,
    };
  }

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      [options.onBehalfOfUserId]: {
        ...team,
        members: [...team.members.filter((member) => member.nodeId !== options.delegateUserId)],
      },
    },
  };
};

const editUserDelegate = (state: CurrentDelegationTeams, payload: EditUserDelegateFulfilledAction['payload']) => {
  if (!state.fulfilled) {
    return state;
  }

  const team = state.fulfilled[payload.onBehalfOfUserId];
  const updatedTeam =
    payload.onBehalfOfUserId === payload.delegateUserId
      ? { ...team, viewerCanPostToStoryline: payload.canPostToStoryline }
      : team;
  const members = updatedTeam?.members;
  const delegateIndex = members?.findIndex((member) => member.nodeId === payload.delegateUserId);
  const delegate = members[delegateIndex];
  if (!delegate) {
    return state;
  }
  const updatedDelegate = { ...delegate, canPostToStoryline: payload.canPostToStoryline };
  const updatedMembers = [
    ...members.slice(0, delegateIndex),
    updatedDelegate,
    ...members.slice(delegateIndex + 1, members.length),
  ];

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      [payload.onBehalfOfUserId]: {
        ...updatedTeam,
        members: updatedMembers,
      },
    },
  };
};

export type DelegationTeamsAction =
  | DelegationTeamsLoadAction
  | AddUserDelegateFulfilledAction
  | EditUserDelegateFulfilledAction
  | RemoveUserDelegateFulfilledAction;

const reducer: Reducer<CurrentDelegationTeams, DelegationTeamsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'DELEGATION_TEAMS.LOAD_PENDING':
      return setPending();
    case 'DELEGATION_TEAMS.LOAD_REJECTED':
      return setRejected(action.payload.error);
    case 'DELEGATION_TEAMS.LOAD_FULFILLED': {
      const delegationTeams = action.payload.entities.delegationTeams;

      return setFulfilled(delegationTeams);
    }
    case 'USER_DELEGATE.ADD_FULFILLED':
      return addUserDelegate(state, action.payload);
    case 'USER_DELEGATE.REMOVE_FULFILLED':
      return removeUserDelegate(state, action.payload);
    case 'USER_DELEGATE.EDIT_FULFILLED':
      return editUserDelegate(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
